// frontend/src/components/JournalEditor/api/journalApi.js
/**
 * Journal API functions for preferences, phrases, attachments, etc.
 */

import apiClient from '../../../apiClient';

// ============== PREFERENCES ==============

export async function getPreferences() {
  const response = await apiClient.get('/api/journal/preferences');
  return response.data;
}

export async function updatePreferences(prefs) {
  const response = await apiClient.put('/api/journal/preferences', prefs);
  return response.data;
}

export async function resetPreferences() {
  const response = await apiClient.post('/api/journal/preferences/reset');
  return response.data;
}

// ============== FRASLEXIKON (personliga fraser, /api/phrases) ==============

// Signal till alla ;-triggers att ladda om lexikonet efter en ändring
export const PHRASES_CHANGED_EVENT = 'aion:phrases-changed';

function notifyPhrasesChanged() {
  window.dispatchEvent(new Event(PHRASES_CHANGED_EVENT));
}

// Returnerar { personal_phrases: [...], clinic_phrases: [], count }
export async function getPhrases() {
  const response = await apiClient.get('/api/phrases');
  return response.data;
}

export async function createPhrase(phrase) {
  const response = await apiClient.post('/api/phrases', phrase);
  notifyPhrasesChanged();
  return response.data;
}

export async function updatePhrase(phraseId, phrase) {
  const response = await apiClient.put('/api/phrases/' + phraseId, phrase);
  notifyPhrasesChanged();
  return response.data;
}

export async function deletePhrase(phraseId) {
  const response = await apiClient.delete('/api/phrases/' + phraseId);
  notifyPhrasesChanged();
  return response.data;
}

// Import av fraslexikon (CSV ur TakeCare: kolumn A = triggerord, B = text).
// Steg 1: förhandsvisning (skriver inget). Steg 2: import med vald dubbletthantering.
export async function previewPhraseImport(file) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/api/phrases/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
}

export async function importPhrases(file, onDuplicate = 'skip') {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('on_duplicate', onDuplicate);
  const response = await apiClient.post('/api/phrases/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  notifyPhrasesChanged();
  return response.data;
}

// ============== ATTACHMENTS ==============

export async function getAttachments(entryId) {
  const response = await apiClient.get('/api/journal/entries/' + entryId + '/attachments');
  return response.data;
}

export async function uploadAttachment(entryId, file, options = {}) {
  const formData = new FormData();
  formData.append('file', file);
  if (options.section_index !== undefined) {
    formData.append('section_index', options.section_index);
  }
  if (options.description) {
    formData.append('description', options.description);
  }
  const response = await apiClient.post(
    '/api/journal/entries/' + entryId + '/attachments',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

export async function deleteAttachment(entryId, attachmentId) {
  // Backend uses /api/journal/attachments/:id for delete
  const response = await apiClient.delete('/api/journal/attachments/' + attachmentId);
  return response.data;
}

export function getAttachmentDownloadUrl(entryId, attachmentId) {
  // Backend uses /api/journal/attachments/:id for download
  return '/api/journal/attachments/' + attachmentId;
}

export async function downloadAttachment(attachmentId) {
  // Download attachment as blob
  const response = await apiClient.get('/api/journal/attachments/' + attachmentId, {
    responseType: 'blob'
  });
  return response.data;
}

// ============== AMENDMENTS ==============

export async function getAmendments(entryId) {
  const response = await apiClient.get('/api/journal/entries/' + entryId + '/amendments');
  return response.data;
}

export async function createAmendment(entryId, amendment) {
  const response = await apiClient.post(
    '/api/journal/entries/' + entryId + '/amendments',
    amendment
  );
  return response.data;
}

// ============== AUDIT LOG ==============

export async function getAuditLog(entryId, options = {}) {
  const params = new URLSearchParams();
  if (options.page) params.set('page', options.page);
  if (options.limit) params.set('limit', options.limit);
  if (options.action) params.set('action', options.action);
  const queryStr = params.toString();
  // Backend uses /audit-log instead of /audit
  const url = queryStr
    ? '/api/journal/entries/' + entryId + '/audit-log?' + queryStr
    : '/api/journal/entries/' + entryId + '/audit-log';
  const response = await apiClient.get(url);
  return response.data;
}

// ============== JOURNAL ENTRIES ==============

export async function saveJournalEntry(entry) {
  if (entry.entry_id) {
    const response = await apiClient.put(
      '/api/journal/entries/' + entry.entry_id,
      entry
    );
    return response.data;
  } else {
    const response = await apiClient.post('/api/journal/entries', entry);
    return response.data;
  }
}

export async function signJournalEntry(entryId) {
  const response = await apiClient.post('/api/journal/entries/' + entryId + '/sign');
  return response.data;
}

export async function searchJournals(params = {}) {
  const searchParams = new URLSearchParams();
  if (params.query) searchParams.set('q', params.query);
  if (params.patient_id) searchParams.set('patient_id', params.patient_id);
  if (params.status) searchParams.set('status', params.status);
  if (params.date_from) searchParams.set('date_from', params.date_from);
  if (params.date_to) searchParams.set('date_to', params.date_to);
  if (params.page) searchParams.set('page', params.page);
  if (params.limit) searchParams.set('limit', params.limit);
  const url = '/api/journal/entries/search?' + searchParams.toString();
  const response = await apiClient.get(url);
  return response.data;
}

export async function getJournalEntry(entryId) {
  const response = await apiClient.get('/api/journal/entries/' + entryId);
  return response.data;
}

export async function getPatientJournalHistory(patientId, options = {}) {
  const params = new URLSearchParams();
  if (options.page) params.set('page', options.page);
  if (options.limit) params.set('limit', options.limit);
  const queryStr = params.toString();
  const url = queryStr
    ? '/api/journal/patients/' + patientId + '/entries?' + queryStr
    : '/api/journal/patients/' + patientId + '/entries';
  const response = await apiClient.get(url);
  return response.data;
}
